const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

// Statuses that mean the payment did not go through. Checkout reports a refusal
// differently depending on where the payment stopped — a bank refusal settles
// the order as `declined`, an abandoned 3DS challenge or wallet sheet as
// `cancelled` — so all of them have to count as terminal. Leaving one out makes
// that payment poll for the full timeout and then report itself as pending.
const FAILED_ORDER_STATUSES = ["failed", "declined", "cancelled"];

const isFailedOrderStatus = (status) => !!status && FAILED_ORDER_STATUSES.indexOf(String(status).toLowerCase()) !== -1;

// Shared "wait for the webhook to finalize the order" poll.
//
// Checkout only tells us the payment was submitted; the order reaches its final
// status when Checkout's webhook reaches our backend.
//
// Only the Flow path uses this so far. The pre-Flow copies of the same loop are
// still live and untouched — CardModal.vue, PaymentModal.vue and two in
// views/TherapistProfile.vue — each on its own 5s setInterval with no timeout
// and no clearInterval on destroy. Migrating them is a follow-up; they are left
// frozen while the Flow rollout is behind a flag.
export default {
  created() {
    // deliberately not in data(): a timer handle never drives the template, and
    // Vue would only pay to observe it
    this.orderPollTimer = null;
  },

  beforeDestroy() {
    this.stopOrderPolling();
  },

  methods: {
    stopOrderPolling() {
      if (this.orderPollTimer) {
        clearTimeout(this.orderPollTimer);
        this.orderPollTimer = null;
      }
    },

    // Polls `read()` — which refreshes the order and resolves with its current
    // status — until that status is terminal. Resolves "ready" | "failed" | "timeout".
    pollOrderStatus(read) {
      this.stopOrderPolling();

      return new Promise((resolve) => {
        const startedAt = Date.now();

        const finish = (result) => {
          this.stopOrderPolling();
          resolve(result);
        };

        const tick = async () => {
          let status;

          try {
            status = await read();
          } catch (e) {
            // a transient read failure is not a final status, but it still has
            // to fall through to the elapsed check below — otherwise an endpoint
            // that keeps failing polls until the component is destroyed
          }

          if (status === "ready") {
            return finish("ready");
          }

          if (isFailedOrderStatus(status)) {
            return finish("failed");
          }

          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            return finish("timeout");
          }

          // Self-scheduled rather than setInterval: a read slower than the
          // interval would otherwise stack overlapping requests for one order.
          this.orderPollTimer = setTimeout(tick, POLL_INTERVAL_MS);
        };

        tick();
      });
    },

    pollSessionOrder(orderId) {
      return this.pollOrderStatus(async () => {
        await this.$store.dispatch("sessions/getOrder", orderId);
        return this.$store.state.sessions.order?.status;
      });
    },

    pollPackageOrder(orderId) {
      return this.pollOrderStatus(async () => {
        await this.$store.dispatch("sessions/checkPackageOrder", orderId);
        return this.$store.state.sessions.package_order?.status;
      });
    }
  }
};
